import * as React from 'react';
import {
  Box,
  Container,
  Grid2 as Grid,
  IconButton,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import dashboard from '../../assets/firm_dashboard.png';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);
const MotionGrid = motion(Grid);

interface LawFirmsSectionProps {
  scrollToContact: () => void;
}

const LawFirmsSection = React.forwardRef<HTMLDivElement, LawFirmsSectionProps>(
  ({ scrollToContact }, ref) => {
    const theme = useTheme();
    const [openModal, setOpenModal] = React.useState(false);

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const handleOpenModal = () => setOpenModal(true);
    const handleCloseModal = () => setOpenModal(false);
    const features = [
      {
        title: 'Plaintiff Acquisition',
        description: 'Efficiently identify and onboard eligible class members.',
      },
      {
        title: 'Case Progress Tracking',
        description:
          'Track all aspects of your cases through each stage of litigation.',
      },
      {
        title: 'Document Management',
        description: 'Secure document collection, verification, and storage.',
      },
      {
        title: 'Real Time Analytics',
        description: 'Use real-time analytics to make data-driven decisions.',
      },
      {
        title: 'Communication Hub',
        description:
          'Centralized communication with all of your plaintiffs. Keep your plaintiffs informed with seamless case updates.',
      },
      {
        title: 'Payment Processing',
        description:
          'Streamline payment processing with a secure toolset that ensures accurate and timely payments, reducing operational risk.',
      },
    ];

    // Animation variants
    const fadeIn = {
      hidden: { opacity: 0, y: 20 },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.6,
          ease: 'easeOut',
        },
      },
    };

    const staggerChildren = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.1,
        },
      },
    };

    const featureItem = {
      hidden: { opacity: 0, x: -20 },
      visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.5 },
      },
    };

    return (
      <Box
        ref={ref}
        sx={{
          pt: { xs: 8, md: 10 },
          pb: { xs: 6, md: 8 },
          bgcolor: 'background.paper',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Container maxWidth="lg">
          <MotionBox
            sx={{ textAlign: 'center', mb: 6 }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeIn}
          >
            <MotionTypography
              variant="h4"
              fontWeight={700}
              sx={{
                mb: 2,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Class Actions, Simplified.
            </MotionTypography>
            <MotionTypography
              variant="h6"
              sx={{ maxWidth: 800, mx: 'auto', color: 'text.secondary' }}
            >
              Streamline class action management and plaintiff communication
              with powerful tools.
            </MotionTypography>
          </MotionBox>

          <Grid container spacing={6} alignItems="center">
            <MotionGrid
              size={{ xs: 12, md: 5 }}
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Box
                sx={{
                  maxWidth: 500,
                  ml: { xs: 'auto', md: 0 },
                  mr: { xs: 'auto', md: 'auto' },
                  position: 'relative',
                  left: { md: '-40px' },
                  top: { md: '-20px' },
                  cursor: 'zoom-in',
                }}
                onClick={handleOpenModal}
              >
                <Box sx={{ position: 'relative' }}>
                  <motion.img
                    src={dashboard}
                    alt="ClassAct for Law Firms"
                    style={{
                      width: isMobile ? '100%' : '115%',
                      display: 'block',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      borderRadius: '12px',
                    }}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                    viewport={{ once: true }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 10,
                      right: 10,
                      backgroundColor: 'rgba(255,255,255,0.7)',
                      borderRadius: '50%',
                      width: 36,
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ZoomInIcon color="primary" />
                  </Box>
                </Box>
              </Box>

              <Modal
                open={openModal}
                onClose={handleCloseModal}
                aria-labelledby="dashboard-modal-title"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    maxWidth: '90%',
                    maxHeight: '90%',
                    outline: 'none',
                  }}
                >
                  <IconButton
                    onClick={handleCloseModal}
                    sx={{
                      position: 'absolute',
                      top: -20,
                      right: -20,
                      backgroundColor: 'background.paper',
                      '&:hover': {
                        backgroundColor: 'background.paper',
                        opacity: 0.9,
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                  <img
                    src={dashboard}
                    alt="ClassAct for Law Firms - Expanded View"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '85vh',
                      display: 'block',
                      borderRadius: '12px',
                      boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                    }}
                  />
                </Box>
              </Modal>
            </MotionGrid>

            <MotionGrid
              size={{ xs: 12, md: 7 }}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerChildren}
            >
              <Box
                sx={{
                  pl: { md: 8 },
                  pt: { md: 3 },
                }}
              >
                <MotionTypography
                  variant="h4"
                  fontWeight={600}
                  sx={{ mb: 4 }}
                  variants={fadeIn}
                >
                  Powerful Case Management
                </MotionTypography>

                <MotionBox
                  sx={{
                    mb: 4,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 3,
                  }}
                  variants={staggerChildren}
                >
                  {features.map((feature, index) => (
                    <MotionBox
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                      }}
                      variants={featureItem}
                    >
                      <VerifiedIcon
                        color="primary"
                        sx={{
                          mt: 0.5,
                          mr: 1.5,
                          fontSize: '1.2rem',
                        }}
                      />
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            mb: 0.5,
                            fontWeight: 600,
                          }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature.description}
                        </Typography>
                      </Box>
                    </MotionBox>
                  ))}
                </MotionBox>
              </Box>
            </MotionGrid>
          </Grid>
        </Container>
      </Box>
    );
  },
);

export default LawFirmsSection;
